import { BLOG_SLUGS, type BlogSlug } from "@listou/shared/constants";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PhoneMockup } from "../components/landing/mockups";
import { CreateCta, FinalCta, STICKER_SPOTS } from "../components/landing/occasion-landing";
import { Sticker } from "../components/list-page/parts";
import {
  BlogCards,
  BlogDownloads,
  FaqSection,
  InlineTry,
  LetterExamples,
  Prose,
  SourcesSection,
  TryListou,
  Verdict,
} from "../components/prose";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { BLOG_POSTS, blogJsonLd, type BlogPost } from "../lib/blog";
import { BLOCK_MARKER } from "../lib/blog-markers";
import { OCCASION_DEMOS, type DemoList } from "../lib/demo";
import { formatDay } from "../lib/format";
import { seo } from "../lib/head";
import { OCCASION_PAGES, type LandingOccasion } from "../lib/occasion-pages";
import { decorStickers, themeStyle } from "../lib/theme";

const isBlogSlug = (value: string): value is BlogSlug =>
  (BLOG_SLUGS as readonly string[]).includes(value);

export const Route = createFileRoute("/blog/$slug")({
  staticData: { ssr: true },
  loader: ({ params }) => {
    if (!isBlogSlug(params.slug)) throw notFound();
    return { slug: params.slug };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const post = BLOG_POSTS[loaderData.slug];
    return seo({
      title: post.seoTitle,
      description: post.seoDescription,
      // The occasion's preview card, rather than the bare icon, when the post is shared.
      image: post.occasion ? OCCASION_PAGES[post.occasion].ogImage : undefined,
      jsonLd: blogJsonLd(post),
    });
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { slug } = Route.useLoaderData();
  const post = BLOG_POSTS[slug];
  // split() with a capture group interleaves the text and the marker names.
  const parts = post.body.split(BLOCK_MARKER);
  const look = post.occasion
    ? { demo: OCCASION_DEMOS[post.occasion], page: OCCASION_PAGES[post.occasion] }
    : null;
  const palette = look ? themeStyle(look.demo.list.theme, null) : undefined;
  const decor = look ? decorStickers(look.demo.list.appearance) : [];

  return (
    <div className="min-h-dvh overflow-x-clip">
      <SiteHeader signedIn={false} />
      <div style={palette} className="bg-page text-ink">
        {look ? (
          <BlogHero post={post} occasion={look.page.occasion} demo={look.demo} />
        ) : (
          <header className="mx-auto grid max-w-3xl gap-5 px-5 pt-6 pb-4 sm:px-8">
            <Breadcrumb label={post.h1} />
            <h1 className="text-4xl font-medium sm:text-5xl">{post.h1}</h1>
            <UpdatedOn day={post.updatedOn} />
            <p className="text-xl leading-relaxed text-ink/85">{post.summary}</p>
          </header>
        )}

        <main className="mx-auto grid max-w-3xl gap-12 px-5 pt-8 pb-20 sm:px-8">
          {post.table && (
            <section className="grid gap-4">
              <h2 className="text-3xl font-semibold">En un coup d'œil</h2>
              <div className="overflow-x-auto rounded-2xl border-[1.5px] border-line">
                <table className="w-full min-w-[48rem] border-collapse text-left">
                  <caption className="sr-only">{post.table.caption}</caption>
                  <thead className="bg-ink/[0.04]">
                    <tr>
                      <th scope="col" className="p-4 font-bold">
                        Service
                      </th>
                      {post.table.columns.map((column) => (
                        <th key={column} scope="col" className="p-4 font-bold">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {post.table.rows.map((row) => (
                      <tr key={row.name} className="border-t border-line align-top">
                        <th scope="row" className="p-4 font-bold">
                          {row.name}
                        </th>
                        {row.cells.map((cell, index) => (
                          <td key={post.table?.columns[index]} className="p-4">
                            <Verdict cell={cell} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {parts.map((part, index) => {
            if (index % 2 === 0) {
              return (
                <Prose key={index} decor={decor}>
                  {part}
                </Prose>
              );
            }
            if (part === "cards" && post.cards) return <BlogCards key={index} cards={post.cards} />;
            if (part === "letters" && post.letters) {
              return <LetterExamples key={index} letters={post.letters} />;
            }
            if (part === "downloads" && post.downloads) {
              return <BlogDownloads key={index} downloads={post.downloads} />;
            }
            if (part === "listou" && post.inlineCta) {
              return (
                <InlineTry
                  key={index}
                  {...post.inlineCta}
                  variant={post.cta}
                  occasion={post.occasion}
                  demo={look?.demo}
                />
              );
            }
            return null;
          })}

          <FaqSection faq={post.faq} />

          {look ? (
            <div className="pt-8">
              <FinalCta
                occasion={look.page.occasion}
                label={look.page.cta}
                stickers={look.page.heroStickers}
                palette={{}}
              >
                Votre liste est prête en deux minutes.
              </FinalCta>
            </div>
          ) : (
            <TryListou variant={post.cta} />
          )}

          {post.sources && post.checkedOn && (
            <SourcesSection
              intro={`Listou est notre service. Chaque information sur les autres services vient de leur propre site, consulté le ${formatDay(post.checkedOn)}. Une erreur ou un changement ? Écrivez-nous, nous corrigeons.`}
              sources={post.sources}
            />
          )}
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}

function Breadcrumb({ label }: { label: string }) {
  return (
    <nav aria-label="Fil d'Ariane" className="text-muted">
      <Link to="/blog" className="font-bold hover:underline">
        Blog
      </Link>
      <span aria-hidden> › </span>
      <span>{label}</span>
    </nav>
  );
}

function UpdatedOn({ day }: { day: string }) {
  return (
    <p className="text-sm text-muted">
      Mis à jour le <time dateTime={day}>{formatDay(day)}</time>
    </p>
  );
}

/**
 * The head of a post about one occasion, dressed like that occasion's landing: its palette,
 * its example list scrolling in a phone, its stickers. The reader sees what they would make
 * before reading how.
 */
function BlogHero({
  post,
  occasion,
  demo,
}: {
  post: BlogPost;
  occasion: LandingOccasion;
  demo: DemoList;
}) {
  const page = OCCASION_PAGES[occasion];
  return (
    <section className="mx-auto grid max-w-6xl gap-12 px-5 pt-6 pb-10 sm:px-8 lg:grid-cols-[1.25fr_1fr] lg:items-center lg:pb-16">
      <div className="grid min-w-0 justify-items-start gap-5">
        <Breadcrumb label={page.linkLabel} />
        <h1 className="font-soft text-[2.3rem] leading-[1.1] font-medium sm:text-5xl">{post.h1}</h1>
        <UpdatedOn day={post.updatedOn} />
        <p className="text-xl leading-relaxed text-ink/80">{post.summary}</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <CreateCta occasion={occasion} label={page.cta} />
          <p className="text-muted">Gratuit, sans compte pour vos proches.</p>
        </div>
      </div>
      <div className="relative mx-auto w-fit">
        <PhoneMockup demo={demo} width={250} scroll={520} className="-rotate-2" />
        {page.heroStickers.map((src, index) => (
          <Sticker
            key={src}
            src={src}
            className={`animate-sticker-pop absolute z-10 drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)] ${STICKER_SPOTS[index]}`}
            style={{ animationDelay: `${150 + index * 110}ms` }}
          />
        ))}
      </div>
    </section>
  );
}
